import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import SeopageSearch from "../containers/Index/Search/SeopageSearch";
import axios from "axios";
import apiUrl from "../common/config/apiUrl";
import apiKey from "../common/config/apiKey";
import fetchLandingProducts from "../common/libs/fetchLandingProducts";
import Seo from "../common/Components/Seo/Seo";
import {
  generateStructuredData,
  optimizeMetaDescription,
  generateCanonicalURL,
  generateBreadcrumbStructuredData,
} from "../common/utils/seoUtils";
import parse from "html-react-parser";
import Loader from "../common/Components/Loader/Loader";

// React Icons
import {
  FaShieldAlt,
  FaHandshake,
  FaBus,
  FaPhoneAlt,
  FaSave,
  FaCheck,
  FaCar,
  FaStar,
  FaAngleDoubleRight,
  FaMapMarkedAlt,
  FaKey,
  FaHeadset,
  FaChevronLeft,
  FaChevronRight,
  FaPlane,
} from "react-icons/fa";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";

const CompanyDetail = () => {
  const { companySlug } = useParams();
  const location = useLocation();
  const pathname = location.pathname;

  const [airports, setAirports] = useState([]);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Derive airport code & name from current path (e.g. /gatwick-airport-parking/..., /heathrow-airport-parking/...)
  const getAirportInfoFromPath = () => {
    if (!pathname) return { code: "", name: "Airport" };

    if (pathname.startsWith("/gatwick-airport-parking")) {
      return { code: "LGW", name: "Gatwick" };
    }
    if (pathname.startsWith("/heathrow-airport-parking")) {
      return { code: "LHR", name: "Heathrow" };
    }
    if (pathname.startsWith("/manchester-airport-parking")) {
      return { code: "MAN", name: "Manchester" };
    }
    if (pathname.startsWith("/stansted-airport-parking")) {
      return { code: "STN", name: "Stansted" };
    }
    if (pathname.startsWith("/birmingham-airport-parking")) {
      return { code: "BHX", name: "Birmingham" };
    }
    if (pathname.startsWith("/bristol-airport-parking")) {
      return { code: "BRS", name: "Bristol" };
    }
    if (pathname.startsWith("/luton-airport-parking")) {
      return { code: "LTN", name: "Luton" };
    }

    return { code: "", name: "Airport" };
  };

  const { code: airportCode, name: airportName } = getAirportInfoFromPath();

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${apiUrl}/airports?key=${apiKey}`);
        if (response?.data?.response_code == 200) {
          setAirports(response?.data?.data);
        }
      } catch (error) {
        console.log("error", error);
      }
    })();
  }, []);

  // Fetch product data from productByAirport API (same as landing tables)
  useEffect(() => {
    if (companySlug && airportCode) {
      fetchProductData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, airportCode]);

  const fetchProductData = async () => {
    try {
      setLoading(true);

      // Use the same API as ParkingTable: /productByAirport with airport + companySlug
      const response = await fetchLandingProducts(airportCode, companySlug);

      if (response?.data) {
        const data = response.data;

        if (Array.isArray(data)) {
          // If API returns a list, find matching slug or take first
          const matchingProduct = data.find((p) => {
            const baseName = p?.name || p?.display_name || "";
            const pSlug = (p?.slug || baseName)
              .toString()
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            return pSlug === companySlug;
          });
          setProduct(matchingProduct || data[0] || null);
        } else {
          // Single product object
          setProduct(data);
        }
      } else {
        setProduct(null);
      }
    } catch (error) {
      console.error("Error fetching product data:", error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return <Loader />;
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="mb-4 text-2xl font-bold text-primary">Company Not Found</h2>
          <p className="text-gray-600">Please search for parking to view company details.</p>
          <Link to="/" className="inline-block px-6 py-3 mt-4 text-white rounded-lg bg-primary hover:bg-blue-700">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  // Safely derive human-readable product type (API sometimes sends object)
  const productType =
    product?.display_name ||
    product?.type_name ||
    (typeof product?.type === "string" ? product.type : "");

  // --- Dynamic SEO for company page ---
  const canonicalUrl = generateCanonicalURL(pathname);

  const metaDescription = optimizeMetaDescription(
    product.short_description ||
      product.details ||
      `${product.name} ${airportName} airport parking. Secure and affordable ${productType || "airport parking"} service.`
  );

  const metaTitleParts = [
    product.name,
    airportName ? `${airportName} Airport Parking` : null,
    productType || null,
    "Airport Parking Deals",
  ].filter(Boolean);
  const metaTitle = metaTitleParts.join(" | ");

  const metaKeywords = [
    product.name,
    airportName && `${airportName} airport parking`,
    productType && `${airportName} ${productType} parking`,
    "airport parking deals",
    "cheap airport parking",
    "UK airport parking",
  ]
    .filter(Boolean)
    .join(", ");

  const breadcrumbStructuredData = generateBreadcrumbStructuredData([
    { name: "Home", url: generateCanonicalURL("/") },
    {
      name: `${airportName} Airport Parking`,
      url: generateCanonicalURL(`/${airportName.toLowerCase()}-airport-parking`),
    },
    { name: product.name, url: canonicalUrl },
  ]);

  const serviceStructuredData = generateStructuredData("service", {
    name: `${product.name} - ${airportName} Airport Parking`,
    description: metaDescription,
    serviceType: productType || "Airport Parking Service",
  });

  // LocalBusiness schema per company (uses contact, email, schedule etc.)
  const openingHoursSpecification = [];
  if (product?.schedule?.days && typeof product.schedule.days === "object") {
    Object.entries(product.schedule.days).forEach(([day, slots]) => {
      if (Array.isArray(slots)) {
        slots.forEach((slot) => {
          const [opens, closesRaw] = slot.split("-");
          const closes = closesRaw === "24:00" ? "23:59" : closesRaw;
          openingHoursSpecification.push({
            "@type": "OpeningHoursSpecification",
            dayOfWeek: day.charAt(0).toUpperCase() + day.slice(1),
            opens,
            closes,
          });
        });
      }
    });
  }

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${canonicalUrl}#localbusiness`,
    name: product.name,
    url: canonicalUrl,
    image: product.image_url || product.image?.src || product.image?.name || undefined,
    address: {
      "@type": "PostalAddress",
      addressCountry: "GB",
    },
    areaServed: {
      "@type": "Airport",
      name: `${airportName} Airport`,
      iataCode: airportCode || undefined,
    },
    currenciesAccepted: "GBP",
    
  };

  if (product.contact) {
    localBusinessSchema.telephone = product.contact;
  }
  if (product.email) {
    localBusinessSchema.email ="info@dummy.co.uk";
  }

  if (openingHoursSpecification.length) {
    localBusinessSchema.openingHoursSpecification = openingHoursSpecification;
  }

  const structuredData = [
    generateStructuredData("organization"),
    generateStructuredData("website"),
    serviceStructuredData,
    breadcrumbStructuredData,
    localBusinessSchema,
  ];

  return (
    <div>
      <Seo
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
        url={canonicalUrl}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
        type="website"
      />

      {/* Search Form Section */}
      <SeopageSearch
        airports={airports}
        airportName={airportName || "Airport"}
        heroTitle={`Book <span class='text-secondary'>${product.name}</span>`}
        description1={product.short_description ? "" : (product.display_name || "Premium parking service")}
        description2=""
        features={[
          {
            title: "100% Protected Parking",
            description: "Peace of mind with fully secured, CCTV-monitored sites",
            icon: <FaShieldAlt />,
          },
          {
            title: "Meet & Greet Service",
            description: "Drop off your car right at the terminal",
            icon: <FaHandshake />,
          },
          {
            title: "Free Shuttle Transfers",
            description: "Fast & comfortable rides to and from terminals",
            icon: <FaBus />,
          },
          {
            title: "24/7 Customer Support",
            description: "We're always here to help, day or night",
            icon: <FaPhoneAlt />,
          },
        ]}
      />

      {/* Breadcrumbs */}
      <section className="py-6 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="px-5 mx-auto max-w-[1280px]">
          <nav className="flex flex-wrap items-center gap-3 text-sm">
            <Link 
              to="/" 
              className="text-gray-600 hover:text-[#0D47A1] transition-colors duration-200 font-medium flex items-center gap-2"
            >
              <span>Home</span>
            </Link>
            <MdKeyboardDoubleArrowRight className="text-[#0277BD] text-lg flex-shrink-0" />
            <Link
              to={`/${airportName.toLowerCase()}-airport-parking`}
              className="text-gray-600 hover:text-[#0D47A1] transition-colors duration-200 font-medium flex items-center gap-2"
            >
              <span>{airportName} Airport Parking</span>
            </Link>
            <MdKeyboardDoubleArrowRight className="text-[#0277BD] text-lg flex-shrink-0" />
            <span className="font-bold text-[#0D47A1] flex items-center gap-2">
              {product.name}
            </span>
          </nav>
        </div>
      </section>

      {/* NEW DESIGN IMPLEMENTATION */}
      <div className="bg-white ">
        
        {/* 1. Top Card Section */}
        <div className="max-w-[1280px] mx-auto px-4 sm:px-5 pt-6 sm:pt-10">
            <div className="bg-[#E0F7FA] p-2 sm:p-3 rounded-xl shadow-sm">
                <div className="bg-[#E0F7FA] border-2 border-dashed border-[#0277BD] rounded-xl p-4 sm:p-6 lg:p-8 flex flex-col lg:flex-row gap-6 lg:gap-8 items-center">
                    {/* Left: Image */}
                    <div className="flex items-center justify-center w-full h-48 p-4 bg-white shadow-sm lg:w-1/3 sm:p-6 rounded-xl sm:h-64">
                        <img
                          src={product.image_url || product.image?.src || product.image?.name}
                          alt={product.name}
                          className="object-contain max-h-full"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                    </div>
                    
                    {/* Right: Content */}
                    <div className="w-full lg:w-2/3">
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0D47A1] mb-3 sm:mb-4">
                            {product.name} <span className="font-normal text-[#1565C0] block sm:inline mt-1 sm:mt-0">- {productType || "Premium Parking"}</span>
                        </h1>
                        
                        {/* Display key points from company short_description or features */}
                        {product.short_description || product.features ? (
                            <div className="mb-6 features-list">
                                <style>{`
                                    .features-list ul {
                                        display: grid;
                                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                                        gap: 0.75rem;
                                        list-style: none;
                                        padding: 0;
                                        margin: 0;
                                    }
                                    @media (max-width: 640px) {
                                        .features-list ul {
                                            grid-template-columns: 1fr;
                                        }
                                    }
                                    .features-list li {
                                        display: flex;
                                        align-items: center;
                                        gap: 0.75rem;
                                        color: #0D47A1;
                                        font-weight: 500;
                                        font-size: 0.875rem;
                                    }
                                    @media (max-width: 640px) {
                                        .features-list li {
                                            font-size: 0.8125rem;
                                        }
                                    }
                                    .features-list li:before {
                                        content: "▶";
                                        color: #0277BD;
                                        flex-shrink: 0;
                                    }
                                `}</style>
                                {parse(product.short_description || product.features)}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 mb-6 sm:grid-cols-2 sm:gap-4">
                                {productType && (
                                    <div className="flex items-center gap-3 text-[#0D47A1] font-medium text-sm sm:text-base">
                                        <MdKeyboardDoubleArrowRight className="text-[#0277BD] text-xl flex-shrink-0" />
                                        <span>{productType}</span>
                                    </div>
                                )}
                                    {Array.isArray(product.terminals) && product.terminals.length > 0 && (
                                    <div className="flex items-center gap-3 text-[#0D47A1] font-medium text-sm sm:text-base">
                                        <MdKeyboardDoubleArrowRight className="text-[#0277BD] text-xl flex-shrink-0" />
                                        <span>Serves {product.terminals.map(t => t.name).join(', ')}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>

        {/* 2. More Info Heading */}
        <div className="max-w-[1280px] mx-auto px-4 sm:px-5 py-8 sm:py-12 text-center">
            <h2 className="text-xl font-extrabold leading-tight text-black uppercase sm:text-2xl lg:text-3xl">
                MORE INFO ON <span className="text-black">{product.name}</span> <br className="hidden sm:block" />
                <span className="text-[#0D47A1] block sm:inline mt-2 sm:mt-0">{productType || "PARKING"} FOR {airportName} AIRPORT</span>
            </h2>
        </div>

        {/* 3. Info Cards (3 Blue Cards) */}
        <div className="max-w-[1280px] mx-auto px-4 sm:px-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {[
                { 
                    icon: <FaCar />, 
                    title: "Premium Parking Service",
                    description: "Experience hassle-free airport parking with our premium services. We offer secure, monitored parking facilities with easy access to all terminals. Your vehicle is in safe hands while you travel."
                },
                { 
                    icon: <FaMapMarkedAlt />, 
                    title: "Convenient Location & Transfer",
                    description: "Strategically located near the airport with quick transfer times. Our complimentary shuttle service ensures you reach your terminal on time, every time. Easy to find with clear directions provided."
                },
                { 
                    icon: <FaKey />, 
                    title: "Secure & Monitored Facilities",
                    description: "Your peace of mind is our priority. All our parking facilities feature 24/7 CCTV surveillance, security patrols, and are fully insured. Park with confidence knowing your vehicle is protected."
                },
            ].map((item, i) => (
                <div key={i} className="bg-[#E1F5FE] p-4 sm:p-6 rounded-xl flex gap-3 sm:gap-4 items-start">
                    <div className="bg-white p-2.5 sm:p-3 rounded-full shadow-sm text-[#FF6F00] text-lg sm:text-xl flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                        {item.icon}
                    </div>
                    <div>
                        <h3 className="font-bold text-[#0D47A1] mb-1.5 sm:mb-2 text-sm sm:text-base">{item.title}</h3>
                        <p className="text-xs leading-relaxed text-gray-600">
                            {item.description}
                        </p>
                    </div>
                </div>
            ))}
        </div>

        {/* 4. Procedures Section */}
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 mb-5 sm:mb-10">
            <style>{`
                .procedure-content ul,
                .procedure-content ol {
                    list-style: none;
                    padding-left: 0;
                    margin: 1rem 0;
                }
                .procedure-content li {
                    position: relative;
                    padding-left: 1.5rem;
                    margin-bottom: 0.75rem;
                    line-height: 1.6;
                }
                .procedure-content li:before {
                    content: "►";
                    position: absolute;
                    left: 0;
                    color: #0D47A1;
                    font-size: 0.75rem;
                    top: 0.25rem;
                }
                .procedure-content p {
                    margin-bottom: 0.75rem;
                    line-height: 1.6;
                }
                .procedure-content strong {
                    color: #0D47A1;
                    font-weight: 600;
                }
                .procedure-content h2,
                .procedure-content h3,
                .procedure-content h4 {
                    color: #0D47A1;
                    font-weight: 700;
                    margin-top: 1.5rem;
                    margin-bottom: 1rem;
                }
            `}</style>

            {/* Arrival Procedure */}
            {product?.arrival_procedure && (
                <div className="mb-2 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-[#0D47A1] mb-4 sm:mb-6 flex items-center gap-2">
                        <MdKeyboardDoubleArrowRight className="text-[#0277BD] text-xl sm:text-2xl flex-shrink-0" />
                        Arrival procedure for {product.name}
                    </h3>
                    <div className="text-xs text-gray-700 procedure-content sm:text-sm">
                        {parse(product.arrival_procedure)}
                    </div>
                </div>
            )}

            {/* Departure Procedure */}
            {product?.departure_procedure && (
                <div className="mb-2 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-[#0D47A1] mb-4 sm:mb-6 flex items-center gap-2">
                        <MdKeyboardDoubleArrowRight className="text-[#0277BD] text-xl sm:text-2xl flex-shrink-0" />
                        Departure procedure for {product.name}
                    </h3>
                    <div className="text-xs text-gray-700 procedure-content sm:text-sm">
                        {parse(product.departure_procedure)}
                    </div>
                </div>
            )}

            {/* Company Details / Overview */}
            {product?.details && (
                <div className="mb-2 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-[#0D47A1] mb-4 sm:mb-6 flex items-center gap-2">
                        <MdKeyboardDoubleArrowRight className="text-[#0277BD] text-xl sm:text-2xl flex-shrink-0" />
                        Company Information
                    </h3>
                    <div className="text-xs text-gray-700 procedure-content sm:text-sm">
                        {parse(product.details)}
                    </div>
                </div>
            )}

 
        </div>

        {/* 5. Reviews Section */}
        {product?.reviews && product.reviews.length > 0 && (
          <div className="py-5 bg-white sm:py-10">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-5">
              <div className="mb-6 text-center sm:mb-10">
                <h2 className="mb-2 text-2xl font-bold text-black sm:text-3xl">REVIEWS</h2>
                <h3 className="text-base sm:text-xl font-bold text-[#0D47A1] uppercase">
                  WHAT OUR CUSTOMERS SAY ABOUT {product.name}
                </h3>
              </div>

              {/* Reviews Grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-6">
                {product.reviews.slice(0, 3).map((review, i) => (
                  <div
                    key={i}
                    className="flex flex-col justify-between p-4 transition-all duration-300 bg-white border border-gray-100 shadow-lg sm:p-6 rounded-2xl hover:shadow-xl hover:-translate-y-1"
                  >
                    <div>
                      <div className="flex text-[#FFC107] mb-3 sm:mb-4 text-sm gap-1">
                        {[...Array(5)].map((_, j) => (
                          <FaStar
                            key={j}
                            className={
                              j < Math.floor(review.overall || 5)
                                ? "text-[#FFC107]"
                                : "text-gray-200"
                            }
                          />
                        ))}
                      </div>
                      <p className="mb-4 text-xs italic leading-relaxed text-gray-600 sm:mb-6 sm:text-sm">
                        "{review.comments}"
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-3 border-t sm:gap-4 sm:pt-4 border-gray-50">
                      <div className="flex items-center justify-center w-8 h-8 text-sm font-bold text-white rounded-full sm:w-10 sm:h-10 sm:text-base bg-secondary">
                        {review.fullname ? review.fullname.charAt(0).toUpperCase() : "A"}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-900 sm:text-sm">
                          {review.fullname || "Anonymous"}
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500">
                          {review.publish_date || "Recent Review"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {product.reviews.length > 3 && (
                 <div className="mt-6 text-center sm:mt-8">
                    <button className="px-5 sm:px-6 py-2 text-xs sm:text-sm font-semibold text-[#0D47A1] border border-[#0D47A1] rounded-full hover:bg-[#0D47A1] hover:text-white transition-colors">
                        View All Reviews
                    </button>
                 </div>
              )}
            </div>
          </div>
        )}

        {/* 6. Bottom Blue Cards - Service Features */}
        <div className="bg-[#E0F7FA] py-10 sm:py-16 mt-8 sm:mt-10">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {[
                    {
                        icon: <FaHeadset />,
                        title: "24/7 Customer Support",
                        subtitle: "Always Here For You",
                        description: "Our dedicated support team is available round the clock to assist you with any queries or concerns."
                    },
                    {
                        icon: <FaShieldAlt />,
                        title: "Secure Parking",
                        subtitle: "Your Safety First",
                        description: "CCTV monitored facilities with security patrols ensuring your vehicle is safe and protected."
                    },
                    {
                        icon: <FaSave />,
                        title: "Flexible Booking",
                        subtitle: "Easy Cancellation",
                        description: "Book with confidence with our flexible cancellation policy and easy amendment options."
                    },
                    {
                        icon: <FaCheck />,
                        title: "Best Price Guarantee",
                        subtitle: "Unbeatable Deals",
                        description: "We guarantee the best prices for airport parking with no hidden charges or fees."
                    }
                ].map((item, i) => (
                                      <div key={i} className="p-2 bg-primary ">

                    <div  className="bg-[#1A237E]  p-5 sm:p-6 text-center relative border-2 h-full border-dashed border-white shadow-xl">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-white rounded-full flex items-center justify-center mb-3 sm:mb-4 text-[#1A237E] text-3xl sm:text-4xl">
                            {item.icon}
                        </div>
                        <h3 className="text-white font-bold text-xs sm:text-sm mb-1.5 sm:mb-2 uppercase">{item.title}</h3>
                        <p className="text-blue-200 text-[10px] sm:text-xs font-bold mb-3 sm:mb-4 uppercase">{item.subtitle}</p>
                        <p className="text-white text-[11px] sm:text-xs leading-relaxed opacity-80">
                            {item.description}
                        </p>
                    </div>
                    </div>
                ))}
            </div>
        </div>

      </div>
    </div>
  );
};

export default CompanyDetail;
